import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Mail, Phone, MessageCircle, CheckCircle, XCircle, Loader2, Send, Settings, Bell
} from 'lucide-react';

export default function NotificationsSettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [config, setConfig] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/notifications/config-status');
      setConfig(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento configurazione');
    } finally {
      setLoading(false);
    }
  };

  const testSMS = async () => {
    if (!testPhone) {
      toast.error('Inserisci un numero di telefono');
      return;
    }
    setSending(true);
    try {
      const response = await api.post(`/notifications/test-sms?phone=${encodeURIComponent(testPhone)}`);
      if (response.data.success) {
        toast.success('SMS di test inviato!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setSending(false);
    }
  };

  const testWhatsApp = async () => {
    if (!testPhone) {
      toast.error('Inserisci un numero di telefono');
      return;
    }
    setSending(true);
    try {
      const response = await api.post(`/notifications/test-whatsapp?phone=${encodeURIComponent(testPhone)}`);
      if (response.data.success) {
        toast.success('WhatsApp di test inviato!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setSending(false);
    }
  };

  const testEmailNotification = async () => {
    if (!testEmail) {
      toast.error('Inserisci un\'email');
      return;
    }
    setSending(true);
    try {
      const response = await api.post(`/notifications/test-email?email=${encodeURIComponent(testEmail)}`);
      if (response.data.success) {
        toast.success('Email di test inviata!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setSending(false);
    }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card>
          <CardContent className="py-8 text-center">
            <XCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h3 className="text-lg font-semibold">Accesso non consentito</h3>
            <p className="text-muted-foreground">Solo il Super Admin può accedere a questa pagina</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="w-6 h-6" />
          Impostazioni Notifiche
        </h1>
        <p className="text-muted-foreground">Configura e testa i canali di notifica</p>
      </div>

      {/* Configuration Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${config?.email?.configured ? 'bg-green-100' : 'bg-red-100'}`}>
                  <Mail className={`w-5 h-5 ${config?.email?.configured ? 'text-green-600' : 'text-red-600'}`} />
                </div>
                <div>
                  <h3 className="font-semibold">Email</h3>
                  <p className="text-sm text-muted-foreground">{config?.email?.provider}</p>
                </div>
              </div>
              <Badge variant={config?.email?.configured ? 'default' : 'destructive'}>
                {config?.email?.configured ? 'Attivo' : 'Non configurato'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${config?.sms?.configured ? 'bg-green-100' : 'bg-red-100'}`}>
                  <Phone className={`w-5 h-5 ${config?.sms?.configured ? 'text-green-600' : 'text-red-600'}`} />
                </div>
                <div>
                  <h3 className="font-semibold">SMS</h3>
                  <p className="text-sm text-muted-foreground">{config?.sms?.provider}</p>
                </div>
              </div>
              <Badge variant={config?.sms?.configured ? 'default' : 'destructive'}>
                {config?.sms?.configured ? 'Attivo' : 'Non configurato'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${config?.whatsapp?.configured ? 'bg-green-100' : 'bg-red-100'}`}>
                  <MessageCircle className={`w-5 h-5 ${config?.whatsapp?.configured ? 'text-green-600' : 'text-red-600'}`} />
                </div>
                <div>
                  <h3 className="font-semibold">WhatsApp</h3>
                  <p className="text-sm text-muted-foreground">{config?.whatsapp?.provider}</p>
                </div>
              </div>
              <Badge variant={config?.whatsapp?.configured ? 'default' : 'destructive'}>
                {config?.whatsapp?.configured ? 'Attivo' : 'Non configurato'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Test Notifications */}
      <Card>
        <CardHeader>
          <CardTitle>Test Notifiche</CardTitle>
          <CardDescription>Invia notifiche di test per verificare la configurazione</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email Test */}
          <div className="flex items-end gap-4">
            <div className="flex-1 space-y-2">
              <Label>Test Email</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="email@esempio.it"
              />
            </div>
            <Button onClick={testEmailNotification} disabled={sending || !config?.email?.configured}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Invia Email
            </Button>
          </div>

          {/* SMS/WhatsApp Test */}
          <div className="flex items-end gap-4">
            <div className="flex-1 space-y-2">
              <Label>Test SMS/WhatsApp</Label>
              <Input
                type="tel"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+39 333 1234567"
              />
            </div>
            <Button onClick={testSMS} disabled={sending || !config?.sms?.configured} variant="outline">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Phone className="w-4 h-4 mr-2" />}
              Invia SMS
            </Button>
            <Button onClick={testWhatsApp} disabled={sending || !config?.whatsapp?.configured} variant="outline">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />}
              Invia WhatsApp
            </Button>
          </div>

          {/* Manual trigger: full expiry check (training + medical + documents) */}
          <div className="border-t pt-4">
            <Label className="text-base font-semibold">Controllo scadenze (manuale)</Label>
            <p className="text-xs text-muted-foreground mt-1 mb-3">
              Forza il controllo di TUTTE le scadenze (formazione, visite mediche, documenti)
              e invia un'email di riepilogo agli amministratori. Da <strong>info@cen-engineering.com</strong>.
            </p>
            <Button
              onClick={async () => {
                try {
                  setSending(true);
                  const r = await api.post('/notifications/check-all-expiries?days=60');
                  const d = r.data || {};
                  toast.success(`Trovate ${d.total_alerts || 0} scadenze (formazione: ${d.training||0}, mediche: ${d.medical||0}, documenti: ${d.documents||0}). Email inviate: ${d.emails_sent||0}`);
                } catch (e) {
                  toast.error('Errore: ' + (e.response?.data?.detail || e.message));
                } finally {
                  setSending(false);
                }
              }}
              disabled={sending}
              data-testid="check-all-expiries-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
              Controlla scadenze e invia riepilogo
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
